import { startOfDay, subDays } from "date-fns";

import { useListTaskInstancesByNurseryQueryControllerList } from "@/generated/compliance-tasks/compliance-tasks";
import type { ComplianceTaskInstanceResponseDto } from "@/generated/model/complianceTaskInstanceResponseDto";
import type { ListTaskInstancesByNurseryQueryControllerListParams } from "@/generated/model/listTaskInstancesByNurseryQueryControllerListParams";
import type { ListTaskInstancesByNurseryQueryControllerListSortBy } from "@/generated/model/listTaskInstancesByNurseryQueryControllerListSortBy";
import type { ListTaskInstancesByNurseryQueryControllerListSortDir } from "@/generated/model/listTaskInstancesByNurseryQueryControllerListSortDir";
import {
  COMPLETED_STATUSES,
  type ComplianceManagerTab,
  type ComplianceSortColumn,
  HISTORY_STATUSES,
  OPEN_STATUSES,
  RECENTLY_COMPLETED_WINDOW_DAYS,
  type SortDir,
} from "@/lib/compliance-manager";

interface UseComplianceManagerDataArgs {
  nurseryId?: string;
  tab: ComplianceManagerTab;
  categoryId?: string;
  sortBy?: ComplianceSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

/** Tab → status/window filters, shared by the list query and the count queries. */
function tabFilters(
  tab: ComplianceManagerTab,
): Pick<
  ListTaskInstancesByNurseryQueryControllerListParams,
  "status" | "completedFrom"
> {
  if (tab === "open") {
    return { status: OPEN_STATUSES };
  }
  if (tab === "recently-completed") {
    return {
      status: COMPLETED_STATUSES,
      completedFrom: startOfDay(
        subDays(new Date(), RECENTLY_COMPLETED_WINDOW_DAYS),
      ).toISOString(),
    };
  }
  // History: the full record excluding not-yet-due (upcoming) tasks. Tasks never
  // reaches here — its list query is disabled.
  return { status: HISTORY_STATUSES };
}

/**
 * Drives the Compliance Manager list server-side: maps the active tab to
 * status/window filters and forwards category, sort and pagination. Tab count
 * badges come from each tab's paginated `total` — the visible tab's list query
 * plus a minimal (limit 1) query per hidden tab, sharing the category filter so
 * the counts always match what switching tabs would show. The Tasks tab lists
 * definitions rather than instances, so it fetches no list here while the count
 * queries keep running to keep the other tabs' badges live.
 */
export function useComplianceManagerData({
  nurseryId,
  tab,
  categoryId,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseComplianceManagerDataArgs) {
  const enabled = !!nurseryId;
  const listEnabled = enabled && tab !== "tasks";
  const baseParams = { categoryId: categoryId || undefined };

  const listQuery = useListTaskInstancesByNurseryQueryControllerList(
    nurseryId ?? "",
    {
      ...baseParams,
      ...tabFilters(tab),
      limit: pageSize,
      offset: page * pageSize,
      includeCompleterNames: true,
      sortBy: sortBy as
        | ListTaskInstancesByNurseryQueryControllerListSortBy
        | undefined,
      sortDir: sortDir as ListTaskInstancesByNurseryQueryControllerListSortDir,
    },
    // refetchOnMount "always": the global QueryClient defaults (staleTime 5min,
    // refetchOnMount false) would otherwise replay a cached page after a task
    // is completed elsewhere — leaving the completed task visible in Open.
    { query: { enabled: listEnabled, refetchOnMount: "always" } },
  );

  // Only the Open and Recently completed tabs show a count badge — History has
  // none (its total is unbounded and not useful at a glance), so we don't fetch
  // a count for it.
  const countTabs = ["open", "recently-completed"] as const;
  const countQueries = countTabs.map((countTab) =>
    // Hook count and order are constant across renders (fixed tab list), so
    // calling in a loop is safe.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useListTaskInstancesByNurseryQueryControllerList(
      nurseryId ?? "",
      { ...baseParams, ...tabFilters(countTab), limit: 1, offset: 0 },
      {
        query: {
          enabled: enabled && countTab !== tab,
          refetchOnMount: "always",
        },
      },
    ),
  );

  const items: ComplianceTaskInstanceResponseDto[] =
    listQuery.data?.data.data ?? [];
  const total: number = listQuery.data?.data.meta.total ?? 0;

  const countFor = (countTab: (typeof countTabs)[number], index: number) =>
    countTab === tab
      ? total
      : (countQueries[index]?.data?.data.meta.total ?? 0);

  return {
    items,
    total,
    isLoading: listQuery.isLoading,
    counts: {
      open: countFor("open", 0),
      "recently-completed": countFor("recently-completed", 1),
    },
  };
}
